import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import '../styles/TodoList.css';

const GLOBAL_VALUE = 'global';

const PERSON_COLORS = [
  { bg: '#e8f5e9', border: '#81c784', badge: '#2e7d32' },
  { bg: '#fff3e0', border: '#ffb74d', badge: '#ef6c00' },
  { bg: '#f3e5f5', border: '#ba68c8', badge: '#7b1fa2' },
  { bg: '#e0f7fa', border: '#4dd0e1', badge: '#00838f' },
  { bg: '#fce4ec', border: '#f06292', badge: '#c2185b' },
  { bg: '#fff8e1', border: '#ffd54f', badge: '#f9a825' },
  { bg: '#efebe9', border: '#a1887f', badge: '#5d4037' },
];

const GLOBAL_COLOR = { bg: '#e3f2fd', border: '#64b5f6', badge: '#1565c0' };
const MINE_COLOR = { bg: '#e8f5e9', border: '#66bb6a', badge: '#2e7d32' };

const displayNameFromEmail = (email = '') => {
  const local = email.split('@')[0] || email;
  return local
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || email;
};

const hashString = (value = '') => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const getPersonColor = (key) => PERSON_COLORS[hashString(key) % PERSON_COLORS.length];

const getTaskStyle = (todo, currentUser) => {
  const scope = todo.scope || 'global';
  if (scope === 'global') {
    return GLOBAL_COLOR;
  }

  const isMine = todo.assignedToUid === currentUser?.uid;
  if (isMine) {
    return MINE_COLOR;
  }

  return getPersonColor(todo.assignedToUid || todo.assignedToEmail || 'assigned');
};

const getCreatedAtMs = (todo) =>
  todo.createdAt?.toMillis?.() || new Date(todo.createdAt || 0).getTime() || 0;

const sortTodos = (list) =>
  [...list].sort((a, b) => {
    const aOrder = typeof a.sortOrder === 'number' ? a.sortOrder : Number.MAX_SAFE_INTEGER;
    const bOrder = typeof b.sortOrder === 'number' ? b.sortOrder : Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return getCreatedAtMs(b) - getCreatedAtMs(a);
  });

const TodoList = () => {
  const [todos, setTodos] = useState([]);
  const [users, setUsers] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [assignee, setAssignee] = useState(GLOBAL_VALUE);
  const [loading, setLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const { currentUser, userData } = useAuth();

  const isDirector = userData?.role === 'director';

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        const approved = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.approved) {
            approved.push({
              id: docSnap.id,
              email: data.email || '',
              name: displayNameFromEmail(data.email || ''),
              role: data.role || '',
            });
          }
        });
        approved.sort((a, b) => a.name.localeCompare(b.name));
        setUsers(approved);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    setLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, 'todos'),
      (querySnapshot) => {
        const todosList = [];
        querySnapshot.forEach((docSnap) => {
          todosList.push({ id: docSnap.id, ...docSnap.data() });
        });
        setTodos(sortTodos(todosList));
        setLoading(false);
      },
      (error) => {
        console.error('Error loading tasks:', error);
        setLoading(false);
        alert('Failed to load tasks. Check Firestore permissions.');
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  const visibleTodos = useMemo(() => {
    if (!currentUser) return [];
    const filtered = isDirector
      ? todos
      : todos.filter((todo) => {
          const scope = todo.scope || 'global';
          if (scope === 'global') return true;
          return todo.assignedToUid === currentUser.uid;
        });
    return sortTodos(filtered);
  }, [todos, currentUser, isDirector]);

  const addTodo = async (e) => {
    e.preventDefault();
    if (!newTodo.trim() || !currentUser) return;

    try {
      const minOrder = todos.reduce((min, todo) => {
        if (typeof todo.sortOrder === 'number') {
          return Math.min(min, todo.sortOrder);
        }
        return min;
      }, 0);

      const payload = {
        text: newTodo.trim(),
        completed: false,
        createdAt: new Date(),
        createdByUid: currentUser.uid,
        createdByEmail: currentUser.email || '',
        sortOrder: minOrder - 1,
      };

      if (assignee === GLOBAL_VALUE) {
        payload.scope = 'global';
        payload.assignedToUid = null;
        payload.assignedToEmail = null;
        payload.assignedToName = null;
      } else {
        const selected = users.find((u) => u.id === assignee);
        if (!selected) {
          alert('Please select a valid user');
          return;
        }
        payload.scope = 'user';
        payload.assignedToUid = selected.id;
        payload.assignedToEmail = selected.email;
        payload.assignedToName = selected.name;
      }

      await addDoc(collection(db, 'todos'), payload);
      setNewTodo('');
      setAssignee(GLOBAL_VALUE);
    } catch (error) {
      console.error('Error adding todo: ', error);
      alert('Failed to add task. Please try again.');
    }
  };

  const toggleTodo = async (id) => {
    try {
      const todo = todos.find((t) => t.id === id);
      await updateDoc(doc(db, 'todos', id), {
        completed: !todo.completed,
      });
    } catch (error) {
      console.error('Error updating todo: ', error);
      alert('Failed to update task. Please try again.');
    }
  };

  const deleteTodo = async (id) => {
    try {
      await deleteDoc(doc(db, 'todos', id));
    } catch (error) {
      console.error('Error deleting todo: ', error);
      alert('Failed to delete task. Please try again.');
    }
  };

  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== dragOverId) {
      setDragOverId(id);
    }
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = draggedId || e.dataTransfer.getData('text/plain');
    setDragOverId(null);
    setDraggedId(null);

    if (!sourceId || sourceId === targetId || savingOrder) return;

    const current = [...visibleTodos];
    const fromIndex = current.findIndex((t) => t.id === sourceId);
    const toIndex = current.findIndex((t) => t.id === targetId);
    if (fromIndex < 0 || toIndex < 0) return;

    const [moved] = current.splice(fromIndex, 1);
    current.splice(toIndex, 0, moved);

    const reordered = current.map((todo, index) => ({
      ...todo,
      sortOrder: index,
    }));

    // Optimistic UI update
    setTodos((prev) => {
      const byId = new Map(reordered.map((t) => [t.id, t.sortOrder]));
      const next = prev.map((todo) =>
        byId.has(todo.id) ? { ...todo, sortOrder: byId.get(todo.id) } : todo
      );
      return sortTodos(next);
    });

    try {
      setSavingOrder(true);
      const batch = writeBatch(db);
      reordered.forEach((todo) => {
        batch.update(doc(db, 'todos', todo.id), { sortOrder: todo.sortOrder });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error saving task order:', error);
      alert('Failed to save task order. Please try again.');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const getUserSwatchColor = (user) => {
    if (user.id === currentUser?.uid) {
      return MINE_COLOR;
    }
    return getPersonColor(user.id || user.email || user.name);
  };

  return (
    <div className="todo-page">
      <div className="todo-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <Link to="/dashboard" className="home-button">
            ← Home
          </Link>
          <h1>Quick Tasks</h1>
        </div>
      </div>

      <div className="todo-layout">
        <aside className="todo-sidebar">
          <h3 className="todo-sidebar-title">Colour key</h3>
          <div className="todo-sidebar-list">
            <div
              className="todo-sidebar-item"
              style={{
                backgroundColor: GLOBAL_COLOR.bg,
                borderColor: GLOBAL_COLOR.border,
                color: GLOBAL_COLOR.badge,
              }}
            >
              Global
            </div>
            {users.map((user) => {
              const color = getUserSwatchColor(user);
              const isYou = user.id === currentUser?.uid;
              return (
                <div
                  key={user.id}
                  className="todo-sidebar-item"
                  style={{
                    backgroundColor: color.bg,
                    borderColor: color.border,
                    color: color.badge,
                  }}
                  title={user.email}
                >
                  {user.name}{isYou ? ' (you)' : ''}
                </div>
              );
            })}
          </div>
        </aside>

        <div className="todo-container">
          <p className="todo-drag-hint">Drag tasks by the ⋮⋮ handle to reorder. New tasks appear at the top.</p>

          <form onSubmit={addTodo} className="todo-form">
            <input
              type="text"
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
              placeholder="Add a new task..."
              className="todo-input"
            />
            <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              className="todo-assignee"
              aria-label="Assign task to"
            >
              <option value={GLOBAL_VALUE}>Global (everyone)</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
            <button type="submit" className="btn btn-primary">
              Add
            </button>
          </form>

          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="todo-list">
              {visibleTodos.length === 0 ? (
                <p className="no-todos">No tasks yet. Add one above!</p>
              ) : (
                visibleTodos.map((todo) => {
                  const style = getTaskStyle(todo, currentUser);
                  const isDragging = draggedId === todo.id;
                  const isDragOver = dragOverId === todo.id && draggedId !== todo.id;

                  return (
                    <div
                      key={todo.id}
                      className={`todo-item ${todo.completed ? 'completed' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                      style={{
                        backgroundColor: style.bg,
                        borderColor: isDragOver ? '#2AA4F7' : style.border,
                      }}
                      onDragOver={(e) => handleDragOver(e, todo.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, todo.id)}
                    >
                      <div
                        className="todo-drag-handle"
                        draggable={!savingOrder}
                        onDragStart={(e) => handleDragStart(e, todo.id)}
                        onDragEnd={handleDragEnd}
                        title="Drag to reorder"
                        aria-label="Drag to reorder"
                      >
                        ⋮⋮
                      </div>
                      <div className="todo-content">
                        <input
                          type="checkbox"
                          checked={todo.completed}
                          onChange={() => toggleTodo(todo.id)}
                          className="todo-checkbox"
                        />
                        <div>
                          <span className="todo-text">{todo.text}</span>
                          {todo.createdByEmail && (
                            <div className="todo-created-by">
                              Added by {todo.createdByEmail}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => deleteTodo(todo.id)}
                        className="btn btn-danger"
                      >
                        Delete
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TodoList;
